/**
 * The header bell's two rules - FRONTEND.md §9.348, the owner's C4.
 *
 * **Both are legacy's**, from `layout.tsx:231`: only an administrator sees it, and the badge caps at
 * `99+`. They are here as functions rather than inline in the component so they can be asserted
 * without mounting a header - the bell polls on a timer, and a test that has to wait for one is a
 * test that is skipped.
 */

/**
 * Who sees the bell.
 *
 * Legacy: `user?.systemRole === "main_admin" || user?.systemRole === "admin"`. It matches the route's
 * own guard on `/notifications`, which is the §9.7 rule - the header may not offer what the router
 * refuses. A cook with a bell that leads to a refusal is worse than no bell.
 */
export function maySeeNotifications(systemRole: string | null | undefined): boolean {
  return systemRole === 'main_admin' || systemRole === 'admin'
}

/**
 * What the badge says, or the empty string when it says nothing.
 *
 * **Nought is not drawn.** A badge reading `0` is a permanent red mark next to the language flag, and
 * the owner asked for the count *"when there is notifications"*. Above 99 it reads `99+`, because
 * three digits do not fit the circle and the difference between 100 and 340 changes nothing about
 * what somebody does next.
 */
export function badgeText(count: number | null | undefined): string {
  if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) return ''
  return count > 99 ? '99+' : String(Math.floor(count))
}

/** The count out of whatever `/notifications/unread-count` answered, or 0. */
export function unreadOf(payload: unknown): number {
  const row = (payload ?? {}) as { count?: unknown }
  return typeof row.count === 'number' && Number.isFinite(row.count) && row.count > 0
    ? Math.floor(row.count)
    : 0
}

/**
 * Whether a notice still counts as new - §9.516.
 *
 * **A predicate rather than `!one.readAt` at the call site**, because the panel asks it three times -
 * for the dot, for the weight, and for whether clicking should mark it - and three spellings of one
 * question is how they drift apart. `agos-one-guard-spelled-twice`.
 *
 * `read_at` is a nullable timestamp, so the server sends `null`; orval types an absent field as
 * `undefined`, and a notice that arrived without the column is not evidence somebody read it.
 */
export function isUnread(one: { readAt?: string | null }): boolean {
  return one.readAt === null || one.readAt === undefined
}
